import React, { useState, useEffect, useCallback } from 'react';
import { authApi, walletApi, barrierApi, rolesApi } from '../../services/api';
import { useT } from '../../i18n';
import toast from 'react-hot-toast';
import { formatDate } from '../../utils/date';

// ── Sabit məlumatlar ──────────────────────────────────────────────────────────
const ROLE_META = {
  super_admin: { label: 'Super Admin', color: '#ef4444' },
  admin:       { label: 'Admin',       color: '#f59e0b' },
  staff:       { label: 'İşçi',        color: '#8b5cf6' },
  client:      { label: 'Müştəri',     color: '#3b82f6' },
  user:        { label: 'İstifadəçi',  color: '#10b981' },
};

const STATUS_META = {
  active:    { label: 'Aktiv',          color: '#10b981', icon: '●' },
  inactive:  { label: 'Deaktiv',        color: 'var(--text3)', icon: '○' },
  suspended: { label: 'Dayandırılıb',   color: '#f59e0b', icon: '⊘' },
  vip:       { label: 'VIP',            color: '#a855f7', icon: '★' },
  pending:   { label: 'Gözləyir',       color: '#06b6d4', icon: '⏳' },
  blocked:   { label: 'Bloklanıb',      color: '#ef4444', icon: '⊗' },
};

const ALL_MODULES = ['barrier', 'restaurant', 'wallet', 'taxi', 'barber', 'news'];
const MODULE_LABELS = { barrier: 'Şlaqbaum', restaurant: 'Restoran', wallet: 'Wallet', taxi: 'Taxi', barber: 'Bərbər', news: 'Xəbərlər' };

const DEFAULT_STAFF_PERMS = {
  barriers:    { view: true,  open: true,  manage: false },
  restaurants: { view: true,  kitchen: true, manage: false },
  wallet:      { view: false, manage: false },
  users:       { view: false, manage: false },
  analytics:   { view: true  },
  settings:    { view: false, manage: false },
  roles:       { view: false, manage: false },
};

const BLANK_STAFF = { fullName: '', phone: '+994', password: '', adminNotes: '', address: '', occupation: '', gender: '', customRole: '', perms: { ...DEFAULT_STAFF_PERMS } };
const BLANK_CLIENT = { fullName: '', phone: '+994', finCode: '', adminNotes: '', address: '', birthDate: '', gender: '', occupation: '', interests: '', allowedModules: ['barrier'] };

// ── Helper komponentlər ────────────────────────────────────────────────────────
function RoleBadge({ role }) {
  const m = ROLE_META[role] || ROLE_META.user;
  return <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 9px', borderRadius: 20, color: m.color, background: m.color + '22' }}>{m.label}</span>;
}

function StatusBadge({ status }) {
  const t = useT();
  const m = STATUS_META[status] || STATUS_META.active;
  const statusKey = 'status_' + (status || 'active');
  return <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 9px', borderRadius: 20, color: m.color, background: m.color + '22' }}>{m.icon} {t(statusKey) || m.label}</span>;
}

function Inp({ label, value, onChange, type = 'text', placeholder, required, style }) {
  return (
    <div style={style}>
      <div style={S.fieldLabel}>{label}{required && ' *'}</div>
      <input style={S.inp} type={type} placeholder={placeholder} value={value} onChange={e => onChange(e.target.value)} />
    </div>
  );
}

function Sel({ label, value, onChange, options }) {
  return (
    <div>
      <div style={S.fieldLabel}>{label}</div>
      <select style={S.inp} value={value} onChange={e => onChange(e.target.value)}>
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </div>
  );
}

function UserRow({ u, selected, onClick }) {
  const t = useT();
  const sm = STATUS_META[u.status] || STATUS_META.active;
  return (
    <div style={{ ...S.userRow, ...(selected ? S.userRowActive : {}), opacity: u.isPreRegistered ? 0.75 : 1 }} onClick={onClick}>
      <div style={{ ...S.ava, background: u.isBlocked ? '#ef444433' : `hsl(${Math.abs(u.id?.charCodeAt(0) || 0) % 360},60%,35%)` }}>
        {(u.fullName?.[0] || '?').toUpperCase()}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ ...S.userName, color: 'var(--text)' }}>
          {u.fullName}
          {u.isPreRegistered && <span style={{ fontSize: 10, color: '#f59e0b', marginLeft: 6 }}>({t('status_pending')})</span>}
        </div>
        <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>
          {u.phone}{u.finCode ? ` · ${u.finCode}` : ''}{u.customRole ? ` · ${u.customRole}` : ''}
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
        <RoleBadge role={u.role} />
        <StatusBadge status={u.status || 'active'} />
      </div>
    </div>
  );
}

// ── Ana komponent ─────────────────────────────────────────────────────────────
export default function Users() {
  const t = useT();
  const [tab, setTab] = useState('all'); // 'all' | 'staff' | 'clients'
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [detailTab, setDetailTab] = useState('info');
  const [transactions, setTransactions] = useState([]);
  const [userBarriers, setUserBarriers] = useState([]);
  const [allBarriers, setAllBarriers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [topupAmount, setTopupAmount] = useState('');
  const [showCreateStaff, setShowCreateStaff] = useState(false);
  const [showCreateClient, setShowCreateClient] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [resetPwModal, setResetPwModal] = useState(null); // user object
  const [resetPwValue, setResetPwValue] = useState('');
  const [resetPwSaving, setResetPwSaving] = useState(false);

  // Forms
  const [staffForm, setStaffForm] = useState(BLANK_STAFF);
  const [staffSaving, setStaffSaving] = useState(false);
  const [clientForm, setClientForm] = useState(BLANK_CLIENT);
  const [clientSaving, setClientSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      let data;
      if (tab === 'staff') data = await authApi.staff();
      else if (tab === 'clients') data = await authApi.clients();
      else data = await authApi.users();
      setUsers(Array.isArray(data) ? data : []);
    } catch { toast.error(t('loading')); }
    finally { setLoading(false); }
  }, [tab]);

  useEffect(() => { load(); }, [tab, load]);
  useEffect(() => {
    barrierApi.list().then(d => setAllBarriers(Array.isArray(d) ? d : [])).catch(() => {});
    rolesApi.list().then(d => setRoles(Array.isArray(d) ? d : [])).catch(() => {});
  }, []);

  const openUser = async (u) => {
    setSelected(u);
    setDetailTab('info');
    setEditMode(false);
    setTransactions([]);
    setUserBarriers([]);
    walletApi.adminTransactions(u.id).then(d => setTransactions(Array.isArray(d) ? d : [])).catch(() => {});
  };

  const loadUserBarriers = useCallback(async (u) => {
    const barriers = allBarriers.length ? allBarriers : await barrierApi.list().catch(() => []);
    const results = await Promise.all(
      (Array.isArray(barriers) ? barriers : []).map(b =>
        barrierApi.getUsers(b.id)
          .then(bu => (Array.isArray(bu) && bu.find(x => x.id === u.id)) ? b : null)
          .catch(() => null)
      )
    );
    setUserBarriers(results.filter(Boolean));
  }, [allBarriers]);

  const handleUpdateRole = async (fields) => {
    try {
      const updated = await authApi.updateRole(selected.id, fields);
      setSelected(updated);
      setUsers(prev => prev.map(u => u.id === updated.id ? updated : u));
      toast.success(t('save'));
    } catch { toast.error('Xəta'); }
  };

  const handleDelete = async (u) => {
    try {
      await authApi.deleteUser(u.id);
      toast.success('Silindi');
      setSelected(null);
      setConfirmDelete(null);
      load();
    } catch { toast.error('Xəta'); }
  };

  const handleResetPassword = async () => {
    if (!resetPwValue || resetPwValue.length < 6) { toast.error('Şifrə minimum 6 simvol olmalıdır'); return; }
    setResetPwSaving(true);
    try {
      await authApi.resetPassword(resetPwModal.id, resetPwValue);
      toast.success(`${resetPwModal.fullName} üçün şifrə sıfırlandı`);
      setResetPwModal(null);
      setResetPwValue('');
    } catch (e) { toast.error(e?.message || 'Xəta'); }
    finally { setResetPwSaving(false); }
  };

  const handleUnlock = async (u) => {
    try {
      await authApi.unlockUser(u.id);
      toast.success(`${u.fullName} hesabı kiliddən açıldı`);
    } catch { toast.error('Xəta'); }
  };

  const handleTopup = async () => {
    const amount = parseFloat(topupAmount);
    if (!amount || amount <= 0) { toast.error('Məbləği daxil edin'); return; }
    try {
      await walletApi.adminTopUp(selected.id, amount, 'Admin əlavəsi');
      toast.success(`${amount} AZN əlavə edildi`);
      setTopupAmount('');
      walletApi.adminTransactions(selected.id).then(d => setTransactions(Array.isArray(d) ? d : [])).catch(() => {});
    } catch { toast.error('Xəta'); }
  };

  const handleCreateStaff = async (e) => {
    e.preventDefault();
    if (!staffForm.fullName || !staffForm.phone || !staffForm.password) { toast.error('Məcburi xanaları doldurun'); return; }
    setStaffSaving(true);
    try {
      await authApi.createStaff({ ...staffForm, staffPermissions: staffForm.perms });
      toast.success('İşçi yaradıldı!');
      setStaffForm(BLANK_STAFF);
      setShowCreateStaff(false);
      load();
    } catch (e) { toast.error(e?.message || 'Xəta'); }
    finally { setStaffSaving(false); }
  };

  const handleCreateClient = async (e) => {
    e.preventDefault();
    if (!clientForm.fullName || !clientForm.phone) { toast.error('Ad və telefon məcburidir'); return; }
    setClientSaving(true);
    try {
      const interests = clientForm.interests ? clientForm.interests.split(',').map(s => s.trim()).filter(Boolean) : [];
      await authApi.createClient({ ...clientForm, interests });
      toast.success('Müştəri yaradıldı!');
      setClientForm(BLANK_CLIENT);
      setShowCreateClient(false);
      load();
    } catch (e) { toast.error(e?.message || 'Xəta'); }
    finally { setClientSaving(false); }
  };

  const filtered = search
    ? users.filter(u => u.fullName?.toLowerCase().includes(search.toLowerCase()) || u.phone?.includes(search) || u.finCode?.includes(search) || u.customRole?.toLowerCase().includes(search.toLowerCase()))
    : users;

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div>
      {/* Header */}
      <div style={S.header}>
        <div>
          <h1 style={{ ...S.title, color: 'var(--text)' }}>{t('users_title')}</h1>
          <p style={{ ...S.sub, color: 'var(--text3)' }}>{t('users_sub')}</p>
        </div>
      </div>

      {/* Tab bar */}
      <div style={S.tabBar}>
        {[['all', t('users_all')], ['staff', t('users_staff')], ['clients', t('users_clients')]].map(([key, label]) => (
          <button key={key} style={{ ...S.tabBtn, ...(tab === key ? S.tabActive : {}), color: tab === key ? 'var(--primary)' : 'var(--text3)' }}
            onClick={() => { setTab(key); setSelected(null); setShowCreateStaff(false); setShowCreateClient(false); }}>
            {label}
          </button>
        ))}
      </div>

      {/* Create buttons — tab içərisindədir */}
      {tab === 'staff' && (
        <div style={{ marginBottom: 16 }}>
          <button style={S.createBtn} onClick={() => setShowCreateStaff(v => !v)}>
            {showCreateStaff ? '✕ ' + t('close') : t('users_create_staff')}
          </button>
        </div>
      )}
      {tab === 'clients' && (
        <div style={{ marginBottom: 16 }}>
          <button style={S.createBtn} onClick={() => setShowCreateClient(v => !v)}>
            {showCreateClient ? '✕ ' + t('close') : t('users_create_client')}
          </button>
        </div>
      )}

      {/* ── İŞÇİ YARAT formu (staff tab içərisində açılır) ── */}
      {tab === 'staff' && showCreateStaff && (
        <form onSubmit={handleCreateStaff} style={{ ...S.formCard, marginBottom: 20 }}>
          <div style={{ ...S.formTitle, color: 'var(--text)' }}>{t('users_create_staff_btn')}</div>
          <p style={{ ...S.formSub, color: 'var(--text3)' }}>{t('users_staff_desc')}</p>

          <div style={S.formGrid}>
            <Inp label={t('name')} value={staffForm.fullName} onChange={v => setStaffForm({...staffForm, fullName: v})} placeholder="Əli Həsənov" required />
            <Inp label={t('phone')} value={staffForm.phone} onChange={v => setStaffForm({...staffForm, phone: v})} type="tel" placeholder="+994501234567" required />
            <Inp label={t('password')} value={staffForm.password} onChange={v => setStaffForm({...staffForm, password: v})} type="password" placeholder="min 6 simvol" required />
            <Inp label={t('address')} value={staffForm.address} onChange={v => setStaffForm({...staffForm, address: v})} placeholder="Bakı, Nərimanov r-nu" />
            <Inp label={t('occupation')} value={staffForm.occupation} onChange={v => setStaffForm({...staffForm, occupation: v})} placeholder="Kassir" />
            <Sel label={t('gender')} value={staffForm.gender} onChange={v => setStaffForm({...staffForm, gender: v})} options={[['', '—'], ['male', t('male')], ['female', t('female')], ['other', t('other')]]} />
            <div>
              <div style={S.fieldLabel}>Xüsusi Rol (opsional)</div>
              <select style={S.inp} value={staffForm.customRole} onChange={e => setStaffForm({...staffForm, customRole: e.target.value})}>
                <option value="">— Rol seç —</option>
                {roles.map(r => <option key={r.id} value={r.name}>{r.icon ? r.icon + ' ' : ''}{r.name}</option>)}
              </select>
            </div>
            <div style={{ gridColumn: '1/-1' }}>
              <div style={S.fieldLabel}>{t('notes')}</div>
              <textarea style={{ ...S.inp, resize: 'vertical', minHeight: 50 }} placeholder={t('users_admin_note_ph')}
                value={staffForm.adminNotes} onChange={e => setStaffForm({...staffForm, adminNotes: e.target.value})} />
            </div>
          </div>

          {/* İcazələr */}
          <div style={{ marginTop: 16 }}>
            <div style={S.fieldLabel}>{t('users_perms')}</div>
            <div style={S.permGrid}>
              {Object.entries(staffForm.perms).map(([module, perms]) => (
                <div key={module} style={{ ...S.permCard, background: 'var(--bg2)', borderColor: 'var(--border)' }}>
                  <div style={{ ...S.permModule, color: 'var(--text2)' }}>{MODULE_LABELS[module] || module}</div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {Object.entries(perms).map(([perm, val]) => (
                      <button key={perm} type="button"
                        style={{ ...S.chip, background: val ? 'var(--primary)22' : 'transparent', color: val ? 'var(--primary)' : 'var(--text3)', borderColor: val ? 'var(--primary)40' : 'var(--border)' }}
                        onClick={() => setStaffForm(prev => ({ ...prev, perms: { ...prev.perms, [module]: { ...prev.perms[module], [perm]: !val } } }))}>
                        {val ? '✓ ' : ''}{perm}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <button style={S.submitBtn} type="submit" disabled={staffSaving}>{staffSaving ? '⟳ ' + t('saving') : '✓ ' + t('users_create_staff_btn')}</button>
        </form>
      )}

      {/* ── MÜŞTƏRİ YARAT formu (clients tab içərisində açılır) ── */}
      {tab === 'clients' && showCreateClient && (
        <form onSubmit={handleCreateClient} style={{ ...S.formCard, marginBottom: 20 }}>
          <div style={{ ...S.formTitle, color: 'var(--text)' }}>{t('users_create_client_btn')}</div>
          <p style={{ ...S.formSub, color: 'var(--text3)' }}>{t('users_client_desc')}</p>

          <div style={S.formGrid}>
            <Inp label={t('name')} value={clientForm.fullName} onChange={v => setClientForm({...clientForm, fullName: v})} placeholder="Əli Həsənov" required />
            <Inp label={t('phone')} value={clientForm.phone} onChange={v => setClientForm({...clientForm, phone: v})} type="tel" placeholder="+994501234567" required />
            <Inp label={t('fin')} value={clientForm.finCode} onChange={v => setClientForm({...clientForm, finCode: v})} placeholder="7 simvol" />
            <Inp label={t('birthday')} value={clientForm.birthDate} onChange={v => setClientForm({...clientForm, birthDate: v})} type="date" />
            <Sel label={t('gender')} value={clientForm.gender} onChange={v => setClientForm({...clientForm, gender: v})} options={[['', '—'], ['male', t('male')], ['female', t('female')], ['other', t('other')]]} />
            <Inp label={t('occupation')} value={clientForm.occupation} onChange={v => setClientForm({...clientForm, occupation: v})} placeholder="Mühəndis" />
            <Inp label={t('address')} value={clientForm.address} onChange={v => setClientForm({...clientForm, address: v})} placeholder="Bakı, Yasamal r-nu" />
            <div style={{ gridColumn: '1/-1' }}>
              <Inp label={t('users_interests') + ' (vergüllə)'} value={clientForm.interests} onChange={v => setClientForm({...clientForm, interests: v})} placeholder="idman, musiqi, texnologiya" style={{ width: '100%' }} />
            </div>
            <div style={{ gridColumn: '1/-1' }}>
              <div style={S.fieldLabel}>{t('notes')}</div>
              <textarea style={{ ...S.inp, resize: 'vertical', minHeight: 50 }} placeholder={t('users_admin_note_ph')}
                value={clientForm.adminNotes} onChange={e => setClientForm({...clientForm, adminNotes: e.target.value})} />
            </div>
          </div>

          {/* Modul icazəsi */}
          <div style={{ marginTop: 16 }}>
            <div style={S.fieldLabel}>{t('users_perms')}</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
              {ALL_MODULES.map(m => {
                const active = clientForm.allowedModules.includes(m);
                return (
                  <button key={m} type="button"
                    style={{ ...S.chip, padding: '7px 14px', background: active ? 'var(--primary)22' : 'transparent', color: active ? 'var(--primary)' : 'var(--text3)', borderColor: active ? 'var(--primary)40' : 'var(--border)' }}
                    onClick={() => setClientForm(prev => ({ ...prev, allowedModules: prev.allowedModules.includes(m) ? prev.allowedModules.filter(x => x !== m) : [...prev.allowedModules, m] }))}>
                    {active ? '✓ ' : ''}{MODULE_LABELS[m]}
                  </button>
                );
              })}
            </div>
          </div>
          <button style={S.submitBtn} type="submit" disabled={clientSaving}>{clientSaving ? '⟳ ' + t('saving') : '✓ ' + t('users_create_client_btn')}</button>
        </form>
      )}

      {/* ── İSTİFADƏÇİ SİYAHISI ── */}
      <div style={S.searchRow}>
        <input style={{ ...S.search, background: 'var(--bg2)', borderColor: 'var(--border)', color: 'var(--text)' }}
          placeholder={t('search')} value={search} onChange={e => setSearch(e.target.value)} />
        <span style={{ ...S.countBadge, color: 'var(--text3)' }}>{filtered.length} {t('users_count')}</span>
      </div>

      <div style={selected ? S.splitLayout : {}}>
        {/* Sol: siyahı */}
        <div style={S.listWrap}>
          {loading ? <div style={S.center}>{t('loading')}</div>
            : filtered.length === 0 ? <div style={{ ...S.empty, color: 'var(--text3)' }}>{t('users_not_found')}</div>
            : filtered.map(u => <UserRow key={u.id} u={u} selected={selected?.id === u.id} onClick={() => openUser(u)} />)
          }
        </div>

        {/* Sağ: detal panel */}
        {selected && (
          <div style={{ ...S.detail, background: 'var(--card)', borderColor: 'var(--border)' }}>
            {/* Header */}
            <div style={S.detailHead}>
              <div style={{ ...S.ava, width: 44, height: 44, fontSize: 20, background: 'linear-gradient(135deg,#3b82f6,#06b6d4)' }}>
                {(selected.fullName?.[0] || '?').toUpperCase()}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>{selected.fullName}</div>
                <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>{selected.phone}</div>
              </div>
              <button style={S.closeBtn} onClick={() => setSelected(null)}>✕</button>
            </div>

            {/* Badges */}
            <div style={{ display: 'flex', gap: 6, marginBottom: 14, flexWrap: 'wrap' }}>
              <RoleBadge role={selected.role} />
              <StatusBadge status={selected.status || 'active'} />
              {selected.isPreRegistered && <span style={{ fontSize: 11, color: '#f59e0b', background: '#f59e0b22', padding: '2px 9px', borderRadius: 20 }}>⏳ {t('status_pending')}</span>}
              {selected.isVerified && <span style={{ fontSize: 11, color: '#10b981', background: '#10b98122', padding: '2px 9px', borderRadius: 20 }}>✓ {t('users_verified')}</span>}
            </div>

            {/* Action buttons */}
            <div style={{ display: 'flex', gap: 6, marginBottom: 14, flexWrap: 'wrap' }}>
              <button style={{ ...S.smBtn, background: 'var(--primary)22', color: 'var(--primary)', border: '1px solid var(--primary)40' }}
                onClick={() => setEditMode(v => !v)}>{editMode ? '✕ ' + t('close') : '✏ ' + t('users_edit')}</button>
              <button style={{ ...S.smBtn, background: '#f59e0b22', color: '#f59e0b', border: '1px solid #f59e0b40' }}
                onClick={() => { setResetPwModal(selected); setResetPwValue(''); }}>🔑 Şifrə Sıfırla</button>
              <button style={{ ...S.smBtn, background: '#10b98122', color: '#10b981', border: '1px solid #10b98140' }}
                onClick={() => handleUnlock(selected)}>🔓 Kilidi Aç</button>
              <button style={{ ...S.smBtn, background: 'var(--danger)22', color: 'var(--danger)', border: '1px solid var(--danger)40' }}
                onClick={() => setConfirmDelete(selected)}>🗑 {t('users_delete')}</button>
            </div>

            {/* Quick status change */}
            <div style={{ marginBottom: 14 }}>
              <div style={S.fieldLabel}>{t('status')}</div>
              <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                {Object.entries(STATUS_META).map(([key, m]) => (
                  <button key={key}
                    style={{ ...S.chip, fontSize: 11, background: (selected.status || 'active') === key ? m.color + '33' : 'transparent', color: m.color, borderColor: m.color + '44' }}
                    onClick={() => handleUpdateRole({ status: key })}>
                    {m.icon} {m.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Detail tabs */}
            <div style={{ display: 'flex', gap: 4, marginBottom: 14, borderBottom: '1px solid var(--border)', paddingBottom: 10 }}>
              {[['info', t('users_details')], ['wallet', t('users_wallet')], ['access', t('users_access')]].map(([k, l]) => (
                <button key={k} style={{ ...S.dtab, ...(detailTab === k ? S.dtabActive : {}), color: detailTab === k ? 'var(--primary)' : 'var(--text3)' }}
                  onClick={() => { setDetailTab(k); if (k === 'access') loadUserBarriers(selected); }}>
                  {l}
                </button>
              ))}
            </div>

            {/* ── Məlumat tab ── */}
            {detailTab === 'info' && (
              editMode ? (
                // Edit rejimi
                <EditInfoForm selected={selected} roles={roles} onSave={handleUpdateRole} t={t} />
              ) : (
                // Baxış rejimi
                <ViewInfo selected={selected} t={t} />
              )
            )}

            {/* ── Wallet tab ── */}
            {detailTab === 'wallet' && (
              <>
                <div style={S.fieldLabel}>{t('users_topup')}</div>
                <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                  <input style={{ ...S.inp, flex: 1 }} type="number" min="1" placeholder="AZN" value={topupAmount} onChange={e => setTopupAmount(e.target.value)} />
                  <button style={S.smBtn} onClick={handleTopup}>+ {t('users_topup_btn')}</button>
                </div>
                <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
                  {[5, 10, 20, 50].map(a => (
                    <button key={a} style={S.chip} onClick={() => setTopupAmount(String(a))}>{a} AZN</button>
                  ))}
                </div>
                <div style={S.fieldLabel}>{t('users_recent_tx')}</div>
                {transactions.length === 0
                  ? <div style={{ color: 'var(--text3)', fontSize: 13 }}>{t('users_no_tx')}</div>
                  : transactions.slice(0, 12).map(tx => (
                    <div key={tx.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid var(--border)' }}>
                      <div>
                        <div style={{ fontSize: 13, color: 'var(--text)' }}>{tx.description}</div>
                        <div style={{ fontSize: 11, color: 'var(--text3)' }}>{formatDate(tx.createdAt, 'compact')}</div>
                      </div>
                      <div style={{ fontWeight: 700, color: tx.type === 'credit' ? 'var(--success)' : 'var(--danger)', fontSize: 13 }}>
                        {tx.type === 'credit' ? '+' : '-'}{Number(tx.amount).toFixed(2)} AZN
                      </div>
                    </div>
                  ))
                }
              </>
            )}

            {/* ── İcazə tab ── */}
            {detailTab === 'access' && (
              <>
                {selected.role === 'client' && (
                  <>
                    <div style={S.fieldLabel}>{t('users_perms')}</div>
                    <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', marginBottom: 16 }}>
                      {ALL_MODULES.map(m => {
                        const active = (selected.allowedModules || []).includes(m);
                        return (
                          <button key={m}
                            style={{ ...S.chip, background: active ? 'var(--primary)22' : 'transparent', color: active ? 'var(--primary)' : 'var(--text3)', borderColor: active ? 'var(--primary)40' : 'var(--border)' }}
                            onClick={() => {
                              const current = selected.allowedModules || [];
                              handleUpdateRole({ allowedModules: active ? current.filter(x => x !== m) : [...current, m] });
                            }}>
                            {MODULE_LABELS[m]}
                          </button>
                        );
                      })}
                    </div>
                  </>
                )}

                <div style={S.fieldLabel}>{t('users_barriers')}</div>
                {userBarriers.length === 0
                  ? <div style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 12 }}>{t('users_no_barriers')}</div>
                  : userBarriers.map(b => (
                    <div key={b.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', borderBottom: '1px solid var(--border)' }}>
                      <div style={{ width: 7, height: 7, borderRadius: '50%', background: b.isOnline ? '#10b981' : '#ef4444', flexShrink: 0 }} />
                      <div style={{ flex: 1, fontSize: 13, color: 'var(--text)' }}>{b.name}</div>
                    </div>
                  ))
                }

                <div style={{ marginTop: 12 }}>
                  <div style={S.fieldLabel}>{t('users_barrier_add')}</div>
                  <select style={S.inp} defaultValue="" onChange={async e => {
                    if (!e.target.value) return;
                    try {
                      await barrierApi.addUser(e.target.value, selected.phone);
                      toast.success('Əlavə edildi');
                      loadUserBarriers(selected);
                    } catch (err) { toast.error(err?.message || 'Xəta'); }
                    e.target.value = '';
                  }}>
                    <option value="">— {t('bar_select')} —</option>
                    {allBarriers.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                  </select>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* ── Reset Password modal ── */}
      {resetPwModal && (
        <div style={S.modal} onClick={() => { setResetPwModal(null); setResetPwValue(''); }}>
          <div style={{ ...S.modalBox, background: 'var(--card)', borderColor: 'var(--border)' }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 32, textAlign: 'center', marginBottom: 12 }}>🔑</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)', textAlign: 'center', marginBottom: 4 }}>Şifrəni Sıfırla</div>
            <div style={{ fontSize: 13, color: 'var(--text3)', textAlign: 'center', marginBottom: 20 }}>{resetPwModal.fullName} · {resetPwModal.phone}</div>
            <div style={S.fieldLabel}>Yeni Şifrə (min 6 simvol)</div>
            <input
              style={{ ...S.inp, marginBottom: 16 }}
              type="password"
              placeholder="Yeni şifrəni daxil edin"
              value={resetPwValue}
              onChange={e => setResetPwValue(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleResetPassword()}
              autoFocus
            />
            <div style={{ display: 'flex', gap: 10 }}>
              <button style={{ ...S.submitBtn, flex: 1, margin: 0, background: '#f59e0b' }} onClick={handleResetPassword} disabled={resetPwSaving}>
                {resetPwSaving ? '⟳ Sıfırlanır...' : '✓ Şifrəni Sıfırla'}
              </button>
              <button style={{ ...S.chip, flex: 1, padding: '10px 0', justifyContent: 'center' }} onClick={() => { setResetPwModal(null); setResetPwValue(''); }}>Ləğv et</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete confirm modal ── */}
      {confirmDelete && (
        <div style={S.modal} onClick={() => setConfirmDelete(null)}>
          <div style={{ ...S.modalBox, background: 'var(--card)', borderColor: 'var(--border)' }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 36, textAlign: 'center', marginBottom: 12 }}>🗑</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)', textAlign: 'center', marginBottom: 8 }}>{t('confirm_delete')}</div>
            <div style={{ fontSize: 13, color: 'var(--text3)', textAlign: 'center', marginBottom: 20 }}>{confirmDelete.fullName}</div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button style={{ ...S.submitBtn, flex: 1, margin: 0, background: 'var(--danger)' }} onClick={() => handleDelete(confirmDelete)}>{t('yes')}, {t('delete')}</button>
              <button style={{ ...S.chip, flex: 1, padding: '10px 0', justifyContent: 'center' }} onClick={() => setConfirmDelete(null)}>{t('cancel')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Baxış komponenti ──────────────────────────────────────────────────────────
function ViewInfo({ selected, t }) {
  const rows = [
    ['ID', selected.id?.slice(0, 8) + '...'],
    [t('name'), selected.fullName],
    [t('phone'), selected.phone],
    [t('fin'), selected.finCode || '—'],
    [t('email'), selected.email || '—'],
    [t('address'), selected.address || '—'],
    [t('birthday'), selected.birthDate ? formatDate(selected.birthDate, 'date') : '—'],
    [t('gender'), selected.gender ? { male: t('male'), female: t('female'), other: t('other') }[selected.gender] : '—'],
    [t('occupation'), selected.occupation || '—'],
    [t('users_interests'), Array.isArray(selected.interests) && selected.interests.length > 0 ? selected.interests.join(', ') : '—'],
    [t('users_custom_role'), selected.customRole || '—'],
    [t('users_last_login'), formatDate(selected.lastLoginAt, 'compact')],
    [t('users_reg_date'), formatDate(selected.createdAt, 'date')],
    [t('notes'), selected.adminNotes || '—'],
  ];

  return (
    <div>
      {rows.map(([k, v]) => (
        <div key={k} style={S.infoRow}>
          <span style={{ ...S.infoKey, color: 'var(--text3)' }}>{k}</span>
          <span style={{ ...S.infoVal, color: 'var(--text2)' }}>{v}</span>
        </div>
      ))}
    </div>
  );
}

// ── Redaktə formu ─────────────────────────────────────────────────────────────
function EditInfoForm({ selected, roles, onSave, t }) {
  const [form, setForm] = useState({
    fullName: selected.fullName || '',
    phone: selected.phone || '',
    finCode: selected.finCode || '',
    email: selected.email || '',
    address: selected.address || '',
    birthDate: selected.birthDate ? new Date(selected.birthDate).toISOString().slice(0, 10) : '',
    gender: selected.gender || '',
    occupation: selected.occupation || '',
    interests: Array.isArray(selected.interests) ? selected.interests.join(', ') : '',
    adminNotes: selected.adminNotes || '',
    customRole: selected.customRole || '',
    role: selected.role || 'user',
    preferredLang: selected.preferredLang || 'az',
  });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      const interests = form.interests ? form.interests.split(',').map(s => s.trim()).filter(Boolean) : [];
      await onSave({ ...form, interests });
    } finally { setSaving(false); }
  };

  return (
    <div>
      <div style={S.formGrid}>
        <Inp label={t('name')} value={form.fullName} onChange={v => setForm({...form, fullName: v})} placeholder="Ad Soyad" />
        <Inp label={t('phone')} value={form.phone} onChange={v => setForm({...form, phone: v})} type="tel" />
        <Inp label={t('fin')} value={form.finCode} onChange={v => setForm({...form, finCode: v})} placeholder="7 simvol" />
        <Inp label={t('email')} value={form.email} onChange={v => setForm({...form, email: v})} type="email" />
        <Inp label={t('address')} value={form.address} onChange={v => setForm({...form, address: v})} placeholder="Ünvan" />
        <Inp label={t('birthday')} value={form.birthDate} onChange={v => setForm({...form, birthDate: v})} type="date" />
        <Sel label={t('gender')} value={form.gender} onChange={v => setForm({...form, gender: v})}
          options={[['', '—'], ['male', t('male')], ['female', t('female')], ['other', t('other')]]} />
        <Inp label={t('occupation')} value={form.occupation} onChange={v => setForm({...form, occupation: v})} placeholder="Peşə" />
        <div style={{ gridColumn: '1/-1' }}>
          <Inp label={t('users_interests') + ' (vergüllə)'} value={form.interests} onChange={v => setForm({...form, interests: v})} placeholder="idman, musiqi" style={{ width: '100%' }} />
        </div>
        <Sel label={t('users_role_change')} value={form.role} onChange={v => setForm({...form, role: v})}
          options={Object.entries({ super_admin: 'Super Admin', admin: 'Admin', staff: 'İşçi', client: 'Müştəri', user: 'İstifadəçi' })} />
        <div>
          <div style={S.fieldLabel}>{t('users_custom_role')}</div>
          <select style={S.inp} value={form.customRole} onChange={e => setForm({...form, customRole: e.target.value})}>
            <option value="">— Seçilməyib —</option>
            {roles.map(r => <option key={r.id} value={r.name}>{r.icon ? r.icon + ' ' : ''}{r.name}</option>)}
          </select>
        </div>
        <Sel label={t('settings_lang')} value={form.preferredLang} onChange={v => setForm({...form, preferredLang: v})}
          options={[['az', 'Azərbaycan'], ['en', 'English'], ['ru', 'Русский']]} />
        <div style={{ gridColumn: '1/-1' }}>
          <div style={S.fieldLabel}>{t('notes')}</div>
          <textarea style={{ ...S.inp, resize: 'vertical', minHeight: 60, width: '100%' }}
            value={form.adminNotes} onChange={e => setForm({...form, adminNotes: e.target.value})} />
        </div>
      </div>
      <button style={S.submitBtn} onClick={submit} disabled={saving}>{saving ? '⟳ ' + t('saving') : '✓ ' + t('settings_save')}</button>
    </div>
  );
}

// ── Stillər ──────────────────────────────────────────────────────────────────
const S = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 },
  title: { fontSize: 24, fontWeight: 800, marginBottom: 4 },
  sub: { fontSize: 13 },
  tabBar: { display: 'flex', gap: 4, marginBottom: 16, background: 'var(--bg2)', borderRadius: 10, padding: 4, border: '1px solid var(--border)', width: 'fit-content' },
  tabBtn: { padding: '7px 18px', borderRadius: 8, border: 'none', background: 'transparent', fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s' },
  tabActive: { background: 'var(--primary)', color: '#fff !important' },
  createBtn: { padding: '9px 20px', background: 'var(--primary)', color: 'white', border: 'none', borderRadius: 10, fontWeight: 700, fontSize: 13, cursor: 'pointer' },
  searchRow: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 },
  search: { flex: 1, padding: '9px 14px', border: '1px solid', borderRadius: 9, fontSize: 13, outline: 'none' },
  countBadge: { fontSize: 12, whiteSpace: 'nowrap' },
  splitLayout: { display: 'grid', gridTemplateColumns: '1fr 380px', gap: 16, alignItems: 'start' },
  listWrap: { display: 'flex', flexDirection: 'column', gap: 6 },
  userRow: { display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', background: 'var(--card)', borderRadius: 10, border: '1px solid var(--border)', cursor: 'pointer', transition: 'all 0.18s' },
  userRowActive: { borderColor: 'var(--primary)', background: 'var(--primary)11' },
  ava: { width: 36, height: 36, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 16, color: 'white', flexShrink: 0 },
  userName: { fontSize: 14, fontWeight: 600 },
  center: { textAlign: 'center', padding: 32, color: 'var(--text3)' },
  empty: { textAlign: 'center', padding: 32 },
  detail: { border: '1px solid', borderRadius: 14, padding: 20, position: 'sticky', top: 20, maxHeight: 'calc(100vh - 120px)', overflowY: 'auto' },
  detailHead: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 },
  closeBtn: { width: 30, height: 30, borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg2)', color: 'var(--text3)', fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' },
  smBtn: { padding: '5px 12px', background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 7, color: 'var(--text2)', fontSize: 12, fontWeight: 600, cursor: 'pointer' },
  chip: { padding: '4px 10px', border: '1px solid', borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer', background: 'transparent' },
  dtab: { padding: '5px 12px', borderRadius: 7, border: 'none', background: 'transparent', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  dtabActive: { background: 'var(--primary)22' },
  infoRow: { display: 'flex', gap: 10, padding: '7px 0', borderBottom: '1px solid var(--border)' },
  infoKey: { fontSize: 12, fontWeight: 600, minWidth: 100, textTransform: 'uppercase', letterSpacing: '0.05em' },
  infoVal: { fontSize: 13, flex: 1 },
  fieldLabel: { fontSize: 11, color: 'var(--text3)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 5 },
  inp: { width: '100%', padding: '9px 12px', background: 'var(--input-bg, var(--bg2))', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text)', fontSize: 13, outline: 'none' },
  formCard: { background: 'var(--card)', borderRadius: 14, padding: 22, border: '1px solid var(--border)' },
  formTitle: { fontSize: 17, fontWeight: 800, marginBottom: 4 },
  formSub: { fontSize: 13, marginBottom: 18, lineHeight: 1.5 },
  formGrid: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 },
  permGrid: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10, marginTop: 8 },
  permCard: { borderRadius: 10, border: '1px solid', padding: 12 },
  permModule: { fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 },
  submitBtn: { marginTop: 18, padding: '11px 24px', background: 'var(--primary)', color: 'white', border: 'none', borderRadius: 10, fontWeight: 700, fontSize: 14, cursor: 'pointer', width: '100%' },
  modal: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999, backdropFilter: 'blur(4px)' },
  modalBox: { borderRadius: 16, padding: 28, border: '1px solid', width: 360, maxWidth: '90vw' },
};
